/**
 * Shared helper — resolve the variation to open when activating a recipe.
 *
 * Rule: prefer the go-to variation; fall back to the first in the list; return
 * undefined when the variations array is empty or absent (the caller must then
 * invoke branch.list to obtain the variation uuid from the backend).
 *
 * This single function is the authoritative implementation for the three
 * activation paths that previously diverged:
 *   1. Library-list click       (app.tsx onActivateRecipe in LibraryView)
 *   2. URL-restore / refresh    (app.tsx urlRestoreAttemptedSlugRef effect)
 *   3. Workspace palette        (Workspace.tsx handleActivateRecipe — already
 *                                correct; refactored here for a shared contract)
 *
 * Invariant: no path may select `variations[0]` without checking isGoTo first.
 *
 * design.md §6 — state preserved on back/forward; the go-to is the canonical
 * representative for any recipe that has not made an explicit variation choice.
 */

import type { Recipe, Variation } from "../explorer/types";

/**
 * Return the go-to variation for `recipe`, or the first variation if no go-to
 * is set, or `undefined` when the variations array is empty / not yet loaded.
 *
 * @example
 *   const v = resolveOpenVariation(recipe);
 *   if (v) {
 *     setCurrentDocKey(`draft:${v.uuid}`);
 *   } else {
 *     // Fallback: fetch from api.branch.list(recipe.uuid)
 *   }
 */
export function resolveOpenVariation(recipe: Pick<Recipe, "variations">): Variation | undefined {
  const variations = recipe.variations;
  if (!variations || variations.length === 0) return undefined;
  return variations.find((v) => v.isGoTo) ?? variations[0];
}
